import { __awaiter, __generator, __read } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { getDocumentThumbnailUrl } from '@office-start/document-service';
import { FileType } from '@office-start/document-types';
import { createCarouselNavClickParams } from '@office-start/feature-instrumentation-utility';
import { getFileClickData, getReferralFromFileType, shouldAddLoginHintToUrl, } from '@office-start/file-utility';
import { addLoginHint, fetchPersonPictureData, } from '@office-start/graph-service';
import { logClickEvent } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { getLaunchInstrumentationParamsFromKnownArea, launchContent, launchUrl, } from '@office-start/launcher-service';
import { useFeedbackInstrumentation, useRemoveRecommendedDocMutation, useSuggestedTasksActionsMutation, } from '@office-start/recommended-data-service';
import { PotentialActionType, } from '@office-start/recommended-types';
import { logError, logFeature, logImpression, logUserAction, } from '@office-start/telemetry-service';
import { getOtelParamsFromInstrumentationParams } from '@office-start/telemetry-utility';
import { useCallback, useMemo } from 'react';
var ClickAction;
(function (ClickAction) {
    ClickAction[ClickAction["Document"] = 0] = "Document";
    ClickAction[ClickAction["JoinMeeting"] = 1] = "JoinMeeting";
    ClickAction[ClickAction["EnterFocusMode"] = 2] = "EnterFocusMode";
    ClickAction[ClickAction["AddToToDo"] = 3] = "AddToToDo";
    ClickAction[ClickAction["MarkAsDone"] = 4] = "MarkAsDone";
    ClickAction[ClickAction["ViewInToDo"] = 5] = "ViewInToDo";
    ClickAction[ClickAction["MeetingInsight"] = 6] = "MeetingInsight";
    ClickAction[ClickAction["BookFocusTime"] = 7] = "BookFocusTime";
})(ClickAction || (ClickAction = {}));
var getEventName = function (clickAction) {
    switch (clickAction) {
        case ClickAction.Document:
            return KnownInstrumentationEvents.RecommendedClick;
        case ClickAction.JoinMeeting:
            return KnownInstrumentationEvents.JoinMeetingClick;
        case ClickAction.AddToToDo:
            return KnownInstrumentationEvents.AddToToDoClick;
        case ClickAction.MarkAsDone:
            return KnownInstrumentationEvents.MarkAsDoneClick;
        case ClickAction.ViewInToDo:
            return KnownInstrumentationEvents.ViewInToDoClick;
        case ClickAction.MeetingInsight:
            return KnownInstrumentationEvents.MeetingInsightsDocClick;
        case ClickAction.BookFocusTime:
            return KnownInstrumentationEvents.CalendarClick;
        case ClickAction.EnterFocusMode:
            return KnownInstrumentationEvents.EnterFocusModeClick;
    }
};
var getAction = function (clickAction, fileType) {
    switch (clickAction) {
        case ClickAction.MeetingInsight:
        case ClickAction.Document: {
            switch (fileType) {
                case FileType.UpcomingMeeting:
                    return 'OpenTeamsMeeting';
                case FileType.MyAnalytics:
                    return 'OpenSuggestedTask';
                default:
                    return 'OpenDocument';
            }
        }
        case ClickAction.JoinMeeting:
            return 'JoinTeamsMeeting';
        case ClickAction.EnterFocusMode:
            return 'EnterFocusMode';
        default:
            return 'OpenSuggestedTask';
    }
};
export function useRecommendedCallbacks(providerType, recommendedData, feedEnabled, viewToDoUrl) {
    var _this = this;
    var _a;
    var context = useOfficeComponentContext().data;
    var _b = __read(useRemoveRecommendedDocMutation(providerType), 1), removeFunction = _b[0];
    var _c = __read(useSuggestedTasksActionsMutation(providerType), 1), performSuggestedTaskActions = _c[0];
    var feedbackInstrumentation = useFeedbackInstrumentation(providerType);
    var onViewToDoClick = useCallback(function (event) {
        var todoUrl = new URL(viewToDoUrl);
        var todoInboxUrl = "".concat(todoUrl.origin).concat(todoUrl.pathname, "/inbox").concat(todoUrl.search);
        var param = getLaunchInstrumentationParamsFromKnownArea('Recommended', getEventName(ClickAction.ViewInToDo));
        if (event === null || event === void 0 ? void 0 : event.defaultPrevented) {
            return;
        }
        event === null || event === void 0 ? void 0 : event.preventDefault();
        launchUrl(todoInboxUrl, { action: 'OpenToDoLink' }, param);
    }, [viewToDoUrl]);
    var onBookFocusTimeClick = useCallback(function (event) {
        var calendarUrl = 'https://outlook.office.com/calendar/view/week';
        var param = getLaunchInstrumentationParamsFromKnownArea('Recommended', getEventName(ClickAction.BookFocusTime));
        param['referral'] = 'Calendar';
        var clickData = {};
        clickData["Referral" /* InstrumentationParam.Referral */] = 'Calendar';
        clickData["RecommendedCardType" /* InstrumentationParam.RecommendedCardType */] = 'Composite';
        clickData["ItemCategory" /* InstrumentationParam.ItemCategory */] = 'officefeed-mru';
        clickData["State" /* InstrumentationParam.State */] = 'Card';
        param['clickParams'] = clickData;
        if (event === null || event === void 0 ? void 0 : event.defaultPrevented) {
            return;
        }
        event === null || event === void 0 ? void 0 : event.preventDefault();
        launchUrl(calendarUrl, { action: 'OpenCalendarLink' }, param);
    }, []);
    var logInstrumentationAndPerformAction = useCallback(function (clickAction, item, index, relativeIndex, area, event, onSetPrimaryAction, cardType) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (item.isDeleted) {
            event === null || event === void 0 ? void 0 : event.preventDefault();
            return;
        }
        if (event === null || event === void 0 ? void 0 : event.defaultPrevented) {
            return;
        }
        event === null || event === void 0 ? void 0 : event.preventDefault();
        var eventName = getEventName(clickAction);
        var clickData = getFileClickData(item, item.id, index, 'Click', area);
        clickData["Referral" /* InstrumentationParam.Referral */] =
            clickAction === ClickAction.JoinMeeting
                ? 'Teams'
                : getReferralFromFileType(item.fileType);
        clickData["ItemId" /* InstrumentationParam.ItemId */] =
            clickAction === ClickAction.MeetingInsight
                ? 'meetinginsight'
                : ((_b = (_a = event === null || event === void 0 ? void 0 : event.currentTarget) === null || _a === void 0 ? void 0 : _a.getAttribute) === null || _b === void 0 ? void 0 : _b.call(_a, 'clickId')) || 'item';
        clickData["RelativeIndex" /* InstrumentationParam.RelativeIndex */] =
            relativeIndex.toString();
        clickData["RecommendedCardType" /* InstrumentationParam.RecommendedCardType */] =
            cardType || '';
        clickData["ItemCategory" /* InstrumentationParam.ItemCategory */] = feedEnabled
            ? 'officefeed-mru'
            : 'recommended-mru';
        clickData["State" /* InstrumentationParam.State */] = 'Card';
        clickData["ResourceId" /* InstrumentationParam.ResourceId */] = item.resourceId || '';
        var launchInstrumentationParams = {
            area: area,
            elementId: item.id,
            eventName: eventName,
            referral: clickData["Referral" /* InstrumentationParam.Referral */],
            clickParams: clickData,
        };
        var contentLaunchParams = {
            action: getAction(clickAction, item.fileType),
            document: item,
            event: event,
            useWebUrl: item.deepLinkUrl ? false : true,
            wdOriginType: item.fileType === 'Stream'
                ? "RecommendedVideo.View" /* WdOriginType.RecommendedVideoView */
                : "REC" /* WdOriginType.Recommended */,
            useDeeplinkUrl: item.deepLinkUrl ? true : false,
        };
        var itemRecommended = item;
        if (clickAction === ClickAction.Document ||
            clickAction === ClickAction.MeetingInsight) {
            launchContent(contentLaunchParams, launchInstrumentationParams);
            (_c = context === null || context === void 0 ? void 0 : context.recordFloodgate) === null || _c === void 0 ? void 0 : _c.call(context, ['ClickedOnDocument']);
            if (clickAction === ClickAction.Document) {
                feedbackInstrumentation({
                    feedbackIds: [item.feedbackId || ''],
                    actions: ['click'],
                    timeStamp: new Date(Date.now()).toISOString(),
                    target: '',
                });
            }
        }
        else if (clickAction == ClickAction.EnterFocusMode) {
            var url = item.fileType === FileType.VivaInsightsFocus
                ? item.joinUrl
                : item.url;
            url &&
                launchUrl(url, contentLaunchParams, launchInstrumentationParams);
        }
        else if (clickAction === ClickAction.JoinMeeting) {
            var url = item.fileType === FileType.UpcomingMeeting
                ? item.joinUrl
                : item.url;
            url =
                url && context && shouldAddLoginHintToUrl(item)
                    ? addLoginHint(url, context)
                    : url;
            url &&
                launchUrl(url, contentLaunchParams, launchInstrumentationParams);
            feedbackInstrumentation({
                feedbackIds: [item.feedbackId || ''],
                actions: ['click'],
                timeStamp: new Date(Date.now()).toISOString(),
                target: 'joinMeeting',
            });
        }
        else if (clickAction === ClickAction.AddToToDo) {
            try {
                performSuggestedTaskActions({
                    feedbackId: (_d = item.feedbackId) !== null && _d !== void 0 ? _d : '',
                    actionType: PotentialActionType.PromoteTask,
                    payload: (_e = (itemRecommended.potentialActions &&
                        itemRecommended.potentialActions[2] &&
                        itemRecommended.potentialActions[2]
                            .actionPayload)) !== null && _e !== void 0 ? _e : '',
                    onViewToDoClick: onViewToDoClick,
                    setPrimaryAction: onSetPrimaryAction,
                });
            }
            catch (_error) {
                logError({
                    eventName: 'Add_To_ToDo_Error',
                    errorType: 'Add_To_ToDo_Error',
                });
                return;
            }
            logUserAction({
                eventName: eventName,
                id: item.id,
                area: area,
                result: 'AddToToDo',
                target: 'Recommended',
            });
        }
        else {
            try {
                performSuggestedTaskActions({
                    feedbackId: (_f = item.feedbackId) !== null && _f !== void 0 ? _f : '',
                    actionType: PotentialActionType.MarkTaskAsDone,
                    payload: (_g = (itemRecommended.potentialActions &&
                        itemRecommended.potentialActions[0] &&
                        itemRecommended.potentialActions[0]
                            .actionPayload)) !== null && _g !== void 0 ? _g : '',
                });
            }
            catch (_error) {
                logError({
                    eventName: 'Mark_Task_As_Done_Error',
                    errorType: 'Mark_Task_As_Done_Error',
                });
                return;
            }
            removeFunction({
                docId: item.id,
                docFileType: item.fileType,
                feedbackId: item.feedbackId,
            });
            logUserAction({
                eventName: eventName,
                id: item.id,
                area: area,
                result: 'MarkAsDone',
                target: 'Recommended',
            });
        }
    }, [
        context,
        feedEnabled,
        feedbackInstrumentation,
        onViewToDoClick,
        performSuggestedTaskActions,
        removeFunction,
    ]);
    var onDocumentClick = useCallback(function (item, index, relativeIndex, area, event, cardType) {
        logInstrumentationAndPerformAction(ClickAction.Document, item, index, relativeIndex, area, event, undefined, cardType);
    }, [logInstrumentationAndPerformAction]);
    var onJoinMeetingClick = useCallback(function (item, index, relativeIndex, area, event) {
        logInstrumentationAndPerformAction(ClickAction.JoinMeeting, item, index, relativeIndex, area, event);
    }, [logInstrumentationAndPerformAction]);
    var onEnterFocusClick = useCallback(function (item, index, relativeIndex, area, event) {
        logInstrumentationAndPerformAction(ClickAction.EnterFocusMode, item, index, relativeIndex, area, event);
    }, [logInstrumentationAndPerformAction]);
    var onMeetingInsightsDocClick = useCallback(function (insight, index, relativeIndex, area, event) {
        logInstrumentationAndPerformAction(ClickAction.MeetingInsight, insight, index, relativeIndex, area, event);
    }, [logInstrumentationAndPerformAction]);
    var onAddToToDoClick = useCallback(function (item, index, relativeIndex, onSetPrimaryAction, area, event) {
        logInstrumentationAndPerformAction(ClickAction.AddToToDo, item, index, relativeIndex, area, event, onSetPrimaryAction);
    }, [logInstrumentationAndPerformAction]);
    var onMarkAsDoneClick = useCallback(function (item, index, relativeIndex, area, event) {
        logInstrumentationAndPerformAction(ClickAction.MarkAsDone, item, index, relativeIndex, area, event);
    }, [logInstrumentationAndPerformAction]);
    var getActivityPersonPicture = useCallback(function (item) { return __awaiter(_this, void 0, void 0, function () {
        var upn, personPictureData, e_1;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 3, , 4]);
                    upn = (_b = (_a = item.activity) === null || _a === void 0 ? void 0 : _a.people[0]) === null || _b === void 0 ? void 0 : _b.upn;
                    if (!upn) return [3 /*break*/, 2];
                    return [4 /*yield*/, fetchPersonPictureData(upn)];
                case 1:
                    personPictureData = (_c.sent()).result;
                    return [2 /*return*/, personPictureData];
                case 2: return [2 /*return*/];
                case 3:
                    e_1 = _c.sent();
                    return [2 /*return*/];
                case 4: return [2 /*return*/];
            }
        });
    }); }, []);
    var getDocumentThumbnail = useCallback(function (item) { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getDocumentThumbnailUrl(item)];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    }); }, []);
    var logFeedbackGeneralViews = useCallback(function (documents, currentPage, increment) {
        var startIndex = (currentPage - 1) * increment;
        var stopIndex = currentPage * increment;
        var currentDocumentsShown = documents.slice(startIndex, stopIndex);
        var feedbackIds = currentDocumentsShown.map(function (i) { return i.feedbackId || ''; });
        var actions = currentDocumentsShown.map(function () { return 'view'; });
        feedbackInstrumentation({
            feedbackIds: feedbackIds,
            actions: actions,
            timeStamp: new Date(Date.now()).toISOString(),
            target: '',
        });
    }, [feedbackInstrumentation]);
    return useMemo(function () {
        return (recommendedData === null || recommendedData === void 0 ? void 0 : recommendedData.documents)
            ? {
                onCarouselNavClick: function (currentPage, increment, totalSize, direction) {
                    var _a, _b, _c;
                    var params = createCarouselNavClickParams(currentPage, increment, totalSize, direction);
                    // log RecommendedImpression
                    logImpression({
                        eventName: "Recommended" /* KnownImpressionInstrumentationEvents.RecommendedImpression */,
                        area: 'Recommended',
                        renderSize: increment,
                        totalSize: totalSize,
                    });
                    logFeedbackGeneralViews(recommendedData.documents, currentPage, increment);
                    var elementId = (_a = params["ElementId" /* InstrumentationParam.ElementId */]) !== null && _a !== void 0 ? _a : '';
                    params["ListId" /* InstrumentationParam.ListId */] = 'recommended';
                    params["ImpressionId" /* InstrumentationParam.ImpressionId */] =
                        (_c = (_b = recommendedData.diagnosticData) === null || _b === void 0 ? void 0 : _b.serverRequestId) !== null && _c !== void 0 ? _c : '';
                    logClickEvent(KnownInstrumentationEvents.RecommendedNavClick, elementId, 'Recommended', undefined, undefined, params);
                    //TODO: Remove above Aria logging once migration is complete
                    logUserAction({
                        id: elementId,
                        eventName: KnownInstrumentationEvents.RecommendedNavClick,
                        result: 'Navigate',
                        target: 'Recommended',
                        area: 'Recommended',
                    });
                    logFeature({
                        featureName: "Recommended" /* KnownFeatureNames.Recommended */,
                        eventName: KnownInstrumentationEvents.RecommendedNavClick,
                        params: getOtelParamsFromInstrumentationParams(params),
                    });
                },
                onDocumentClick: onDocumentClick,
                onMeetingInsightsDocClick: onMeetingInsightsDocClick,
                getActivityPersonPicture: getActivityPersonPicture,
                getDocumentThumbnail: getDocumentThumbnail,
                onJoinMeetingClick: onJoinMeetingClick,
                onEnterFocusClick: onEnterFocusClick,
                onAddToToDoClick: onAddToToDoClick,
                onMarkAsDoneClick: onMarkAsDoneClick,
                onViewToDoClick: onViewToDoClick,
                onBookFocusTimeClick: onBookFocusTimeClick,
            }
            : undefined;
    }, [
        recommendedData === null || recommendedData === void 0 ? void 0 : recommendedData.documents,
        (_a = recommendedData === null || recommendedData === void 0 ? void 0 : recommendedData.diagnosticData) === null || _a === void 0 ? void 0 : _a.serverRequestId,
        onDocumentClick,
        onMeetingInsightsDocClick,
        getActivityPersonPicture,
        getDocumentThumbnail,
        onJoinMeetingClick,
        onEnterFocusClick,
        onAddToToDoClick,
        onMarkAsDoneClick,
        onViewToDoClick,
        onBookFocusTimeClick,
        logFeedbackGeneralViews,
    ]);
}
//# sourceMappingURL=useRecommendedCallbacks.js.map